import { DOCUMENT, Location } from '@angular/common';
import { inject, Injectable, InjectionToken, Injector } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { IngestionStore } from '../../vault/store/ingestion.store';
import { isChunkLoadFailure } from '../utils/chunk-load-failure.util';

export const NAV_ERROR_RELOAD_STORAGE_KEY = 'indago-nav-error-reload';
export const NAV_ERROR_TOAST_KEY = 'nav-error';

export interface NavigationReloadMarker {
  targetUrl: string;
  at: number;
}

export interface NavigationRecoveryBrowser {
  isOnline(): boolean;
  assignLocation(url: string): void;
  storage(): Storage | null;
}

type RecoveryAction = 'reload' | 'toast' | 'none';

function windowRecoveryBrowser(view: Window | null): NavigationRecoveryBrowser {
  return {
    isOnline: () => view?.navigator.onLine !== false,
    assignLocation: (url) => view?.location.assign(url),
    storage: () => {
      try {
        return view?.sessionStorage ?? null;
      } catch {
        return null;
      }
    },
  };
}

function describeError(error: unknown): string {
  try {
    const message = (error as { message?: unknown } | null)?.message;
    return typeof message === 'string' ? message : String(error);
  } catch {
    return '';
  }
}

export const NAVIGATION_RECOVERY_BROWSER = new InjectionToken<NavigationRecoveryBrowser>('NAVIGATION_RECOVERY_BROWSER', {
  providedIn: 'root',
  factory: () => windowRecoveryBrowser(inject(DOCUMENT).defaultView),
});

/** True while an automatic reload would destroy work in progress: a vault upload lives in a root store and dies with the page. */
export const NAVIGATION_RELOAD_BLOCKER = new InjectionToken<() => boolean>('NAVIGATION_RELOAD_BLOCKER', {
  providedIn: 'root',
  factory: () => {
    const injector = inject(Injector);
    return () => injector.get(IngestionStore).isUploading();
  },
});

/**
 * Recovers from a lazy chunk that no longer exists after a deploy. An automatic reload needs either
 * no reload marker or a successful navigation since the page loaded, so two automatic reloads always
 * have a success between them and a chunk that stays broken ends at the toast, never in a loop.
 */
@Injectable({ providedIn: 'root' })
export class NavigationErrorRecoveryService {
  private readonly browser = inject(NAVIGATION_RECOVERY_BROWSER);
  private readonly location = inject(Location);
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly reloadBlocked = inject(NAVIGATION_RELOAD_BLOCKER);

  public handle(event: NavigationError): void {
    try {
      this.recover(event);
    } catch {
      console.error('[nav-error-recovery-failed]', { url: event?.url });
    }
  }

  public reloadTo(routerUrl: string): void {
    this.writeMarker(routerUrl);
    this.browser.assignLocation(this.location.prepareExternalUrl(routerUrl));
  }

  private recover(event: NavigationError): void {
    const chunk = isChunkLoadFailure(event.error);
    const action: RecoveryAction = chunk ? this.chooseChunkAction(event.url) : 'none';
    console.warn('[nav-error]', { kind: chunk ? 'chunk' : 'other', action, url: event.url, message: describeError(event.error) });
    if (action === 'reload') {
      this.browser.assignLocation(this.location.prepareExternalUrl(event.url));
    } else if (action === 'toast') {
      this.showToast(event.url);
    }
  }

  /**
   * Writes the marker for a reload it allows: a reload whose marker cannot be stored could repeat forever.
   * Any throw ends at the toast, because the user can still reload from it and a reload cannot be undone.
   */
  private chooseChunkAction(targetUrl: string): RecoveryAction {
    try {
      const storage = this.browser.storage();
      if (!this.browser.isOnline() || !storage || this.reloadBlocked()) {
        return 'toast';
      }
      const mayReload = storage.getItem(NAV_ERROR_RELOAD_STORAGE_KEY) === null || this.router.lastSuccessfulNavigation() !== null;
      return mayReload && this.writeMarker(targetUrl) ? 'reload' : 'toast';
    } catch {
      return 'toast';
    }
  }

  private writeMarker(targetUrl: string): boolean {
    try {
      const marker: NavigationReloadMarker = { targetUrl, at: Date.now() };
      const storage = this.browser.storage();
      storage?.setItem(NAV_ERROR_RELOAD_STORAGE_KEY, JSON.stringify(marker));
      return storage !== null;
    } catch {
      return false;
    }
  }

  private showToast(targetUrl: string): void {
    this.messageService.clear(NAV_ERROR_TOAST_KEY);
    this.messageService.add({ key: NAV_ERROR_TOAST_KEY, sticky: true, severity: 'warn', data: { targetUrl } });
  }
}

/** Router navigation-error handler. Must never throw: a throw here replaces the navigation's own error. */
export function recoverFromNavigationError(event: NavigationError): void {
  try {
    inject(NavigationErrorRecoveryService).handle(event);
  } catch {
    console.error('[nav-error-recovery-failed]', { url: event?.url });
  }
}
